import { useMemo, type ReactNode } from "react";
import { useInvitationContext } from "../context/InvitationContext";
import { useBusinessContext } from "../../../context/BusinessContext";
import { useBusinessById } from "../../../hooks/useBusinessCrud";
import { S3Image } from "../../../components/S3Image";
import { Button } from "../../../components/ui/Button";
import { Notice } from "../../../components/ui/Notice";
import { useT } from "../../../i18n/admin";
import { isPassFeeService } from "./serviceBooking";
import { formatPhoneForDisplay } from "../../../helpers/phoneUtils";

// SAM-1279 — the Confirm step of New pass, as approved in the admin canvas (PassConfirm, 26 Oct): where the guest
// goes (the real destination, with its logo), then one row per fact the operator is about to commit, then the
// button. Tailwind + tokens only (SAM-175 freeze).

type Props = {
  disableConfirm?: boolean;
  isLoading?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  /** Why the server refused to create the pass; shown above the button. */
  error?: string | null;
};

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[96px_minmax(0,1fr)] sm:grid-cols-[160px_minmax(0,1fr)] items-center gap-3 text-[14px]">
    <span style={{ color: "var(--ink-500)" }}>{label}</span>
    <span style={{ color: "var(--ink-900)" }}>{children}</span>
  </div>
);

export const InvitationDetail = ({ disableConfirm, isLoading, onConfirm, error }: Props) => {
  const { invitation } = useInvitationContext();
  const { business } = useBusinessContext();
  const { t, fmt } = useT();

  // The tenant Front Desk has no context business: the one picked as destination names the card (and gives its logo).
  const destination = invitation?.location?.estate_id?.[0] as { id?: string | number; label?: string; isBusiness?: boolean } | undefined;
  const destinationBusinessId = destination?.isBusiness && destination.id ? String(destination.id) : business?.id ?? null;
  const { data: destinationBusiness } = useBusinessById(destinationBusinessId);
  const logoUrl = destinationBusiness?.[0]?.logo_url ?? (destinationBusinessId === business?.id ? business?.logo_url : undefined);
  const destinationName = destination?.label || destinationBusiness?.[0]?.name || business?.name || "";

  const service = invitation?.selectedService ?? null;
  const detailLine = service?.name_en || service?.name || invitation?.accessType?.[0]?.label || "";

  const dates = useMemo(() => {
    const d = invitation?.duration;
    if (d?.startDate && d?.endDate) return `${fmt.date(String(d.startDate).slice(0, 10))} – ${fmt.date(String(d.endDate).slice(0, 10))}`;
    const single = d?.date || invitation?.serviceBooking?.date;
    return single ? fmt.date(String(single).slice(0, 10)) : "";
  }, [invitation?.duration, invitation?.serviceBooking?.date, fmt]);

  const mainGuest = invitation?.guests?.mainGuest;
  const guestName = `${mainGuest?.firstName || ""} ${mainGuest?.lastName || ""}`.trim();
  const adults = invitation?.guests?.companions?.adults ?? 0;
  const children = invitation?.guests?.companions?.childrens ?? 0;
  const guests = [
    adults > 0 ? t(adults === 1 ? "booking.summary.adultOne" : "booking.summary.adultMany", { n: adults }) : "",
    children > 0 ? t(children === 1 ? "booking.summary.childOne" : "booking.summary.childMany", { n: children }) : "",
  ].filter(Boolean).join(", ");

  // SAM-1312 (U35): «Entered» only when a document number was entered; with none the row is not shown.
  const hasIdNumber = Boolean(mainGuest?.idNumber || mainGuest?.idnum);
  const idDocument = mainGuest?.captureMode === "share_link"
    ? t("passConfirm.idByLink")
    : invitation?.guestInputModes?.[`${mainGuest?.firstName}-${mainGuest?.lastName}`] === "scan"
      ? t("passConfirm.idScanned")
      : hasIdNumber
        ? t("passConfirm.idEntered")
        : "";

  const cardNumber = invitation?.creditCard?.cardNumber;
  // A pass_fee service is paid by the guest through a payment link (SAM-1171): no card is taken here.
  const payment = isPassFeeService(invitation?.selectedService)
    ? t("passConfirm.link")
    : cardNumber && cardNumber.length >= 4 ? t("passConfirm.card", { last4: cardNumber.slice(-4) }) : "";

  return (
    <section
      className="flex flex-col gap-4 p-6"
      style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: "var(--r-12)" }}
    >
      <h2 className="m-0 text-[18px] font-bold" style={{ color: "var(--ink-900)" }}>{t("passConfirm.title")}</h2>

      <div
        className="flex items-center gap-3.5 p-4"
        style={{ border: "1px solid var(--line)", borderRadius: "var(--r-12)", background: "var(--surface)" }}
      >
        <span
          className="flex items-center justify-center shrink-0 overflow-hidden text-[19px] font-bold"
          style={{ width: 52, height: 52, borderRadius: "var(--r-8)", background: "var(--tenant-primary, #005488)", color: "#fff" }}
        >
          {logoUrl ? (
            <S3Image imageKey={logoUrl} alt={destinationName} style={{ width: "100%", height: "100%", objectFit: "cover" }} fallbackComponent={initialsOf(destinationName)} />
          ) : (
            initialsOf(destinationName)
          )}
        </span>
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wide" style={{ color: "var(--ink-400)" }}>{t("passConfirm.destination")}</div>
          <div className="text-[17px] font-bold truncate" style={{ color: "var(--ink-900)" }}>{destinationName}</div>
          {detailLine && <div className="text-[13px] truncate" style={{ color: "var(--ink-500)" }}>{detailLine}</div>}
        </div>
      </div>

      <Row label={t("passConfirm.mainGuest")}>{[guestName, mainGuest?.phoneNumber ? formatPhoneForDisplay(mainGuest.phoneNumber) : ""].filter(Boolean).join(" · ")}</Row>
      {dates && <Row label={t("passConfirm.dates")}>{dates}</Row>}
      {guests && <Row label={t("passConfirm.guests")}>{guests}</Row>}
      {idDocument && <Row label={t("passConfirm.idDocument")}>{idDocument}</Row>}
      {payment && <Row label={t("passConfirm.payment")}>{payment}</Row>}

      {error && <Notice variant="error">{error}</Notice>}

      <div>
        <Button variant="primary" className="w-full justify-center sm:w-auto" onClick={onConfirm} disabled={disableConfirm} isLoading={isLoading}>
          {t("passConfirm.continue")}
        </Button>
      </div>
    </section>
  );
};
